// The workforce report: everyone and every role in the organisation, what they cost, how much work they have against
// their capacity, who they hand work to, which systems they use, and where people could help one another. Built from
// the organisation model (roles, people and who fills which role) and the analysis graph (the work, its roles and
// systems, and how it flows), so it can be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { EDGE_TYPE, FREQUENCY_TYPE, getInsightsForNode, GraphData, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, WORK_TYPE } from '../common/linqset-model';

// friction work, as the EPM scorecard counts a step's whole time as friction: exception work (on a detour from the main
// flow, or marked as such) and work marked as not adding value
export const isFrictionWork = (a: AnalysisNode) => a.exceptionWork || a.workKind === WORK_TYPE.NOT_VALUE_ADDING;

export type WorkforceStatus = 'over' | 'stretched' | 'healthy' | 'under' | 'unknown';

export const WORKFORCE_STATUS_LABELS: Record<WorkforceStatus, string> = {
    over: 'Over capacity',
    stretched: 'Stretched',
    healthy: 'Healthy',
    under: 'Spare capacity',
    unknown: 'Capacity not set',
};

// utilisation bands: over capacity, close to it, comfortable, and with much spare capacity
export const OVER = 1;
export const STRETCHED = 0.85;
export const UNDER = 0.5;
// a person spread across this many roles or more
export const SPREAD_ROLES = 4;
// spare hours a year worth offering to another role
export const MIN_SPARE_HOURS = 80;

export interface RoleSummary {
    key: string;
    name: string;
    external: boolean;                  // an external organisation, whose capacity isn't the organisation's to manage
    headcount: number;
    peopleKeys: string[];               // the people (or teams) who fill it
    hours: number;                      // work a year (in a scope, its work in the scope's models)
    otherHours: number;                 // in a scope, its work a year in the organisation's other models; 0 otherwise
    capacity: number | null;            // hours a year, or null when not set
    capacityFromDefault: boolean;       // the capacity is the organisation's full-time year for each of its people
    utilisation: number | null;         // all its work, in the scope and elsewhere, as a share of capacity
    status: WorkforceStatus;
    cost: number;                       // annual cost
    idleCost: number;                   // of that, the cost of capacity no work uses
    actions: number;                    // actions it performs
    frictionActions: number;            // of those, friction work: exception work, and work marked as not adding value
    frictionHours: number;             // its share of their hours a year
    frictionCost: number;              // and of their cost
    models: string[];                   // the models it works in
    systems: { key: string; name: string; hours: number }[];   // the systems it uses, most used first
    contribution: ValueContribution;    // the value and revenue its work creates (in a scope, in the scope's models)
}

export interface PersonSummary {
    key: string;
    name: string;
    team: boolean;
    headcount: number;
    roles: { key: string; name: string; share: number }[];   // the roles they fill, with the share of their time on each
    hours: number;
    capacity: number;
    utilisation: number | null;
    status: WorkforceStatus;
    cost: number;
    flags: string[];                    // what to look at, in a few words
}

export type FindingSeverity = 'critical' | 'warning' | 'info';

export interface WorkforceFinding {
    severity: FindingSeverity;
    title: string;
    detail: string;
    roleKey?: string;
    personKey?: string;
    weight: number;                     // how much it matters, to rank by (hours or money)
}

export interface WorkforceMatch {
    roleKey: string;
    role: string;
    reason: 'over' | 'single';          // the role is over capacity, or only one person can do it
    candidates: { key: string; name: string; currentRoles: string[]; spareHours: number; similarity: number; sharedSystems: string[]; sharedModels: string[] }[];
}

export interface WorkforceMatrix {
    rows: { key: string; name: string }[];
    columns: { key: string; name: string }[];
    values: number[][];                 // rows by columns
    max: number;
}

export interface WorkforceReport {
    totals: {
        headcount: number;
        people: number;                 // people and teams in the model
        roles: number;
        cost: number;
        hours: number;                  // work, on roles whose capacity is known (in a scope, the work in its models)
        otherHours: number;             // in a scope, those roles' work in the organisation's other models
        capacity: number;               // capacity, on roles whose capacity is known
        utilisation: number | null;     // all that work as a share of the capacity
        spareHours: number;
        spareCost: number;
        overRoles: number;
        unknownRoles: number;           // roles without capacity set
        defaultRoles: number;           // roles whose capacity is the organisation's full-time year
        frictionActions: number;        // actions that are friction work, with their hours and cost a year
        frictionHours: number;
        frictionCost: number;
        frictionRoles: number;         // roles doing any of it
        valueContribution: number;      // the value and revenue the roles' work creates, external roles' included
        revenueContribution: number;
        workCost: number;               // the cost of that work
    };
    scoped: boolean;                    // for some of the organisation's models rather than all of them
    findings: WorkforceFinding[];
    roles: RoleSummary[];
    people: PersonSummary[];
    handoffs: WorkforceMatrix;          // items a year passed from one role's work to another's
    topHandoffs: { from: string; to: string; items: number }[];
    systems: WorkforceMatrix;           // hours a year each role works with each system
    matches: WorkforceMatch[];
}

export interface WorkforceInput {
    organisationModel: GraphData;
    analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] };
    workspaceKey: string;
    modelNames: { [modelKey: string]: string };
    defaultCapacityHours?: number;      // the organisation's full-time hours a year, for roles without capacity set
    // the analysis is some of the organisation's models rather than all of them: a role's work is what it does in
    // them, and roles and people with no work in them are left out
    scoped?: boolean;
    money: (value: number) => string;
    number: (value: number) => string;
}

export const total = (a: { allocated?: number; unallocated?: number; unmodelled?: number } | undefined) =>
    a ? (a.allocated || 0) + (a.unallocated || 0) + (a.unmodelled || 0) : 0;

// the value and revenue a role or system contributes (its share of what the work it does creates, so they add up across
// roles and across systems) and the cost of that work, in the given models or in all of them. Its insights for each
// model hold its contribution and work cost there
export interface ValueContribution {
    value: number;
    revenue: number;
    workCost: number;
    revenueReturn: number | null;       // revenue contributed for each unit of the work's cost, when it has a cost
}

export const contributionOf = (n: LinqsetNode, workspaceKey: string, modelKeys?: Set<string>): ValueContribution => {
    const insights = (n.insights || []).filter(i => i.projectKey === workspaceKey && i.modelKey !== ORGANISATION_MODEL_KEY && (!modelKeys || modelKeys.has(i.modelKey)));
    const sum = (f: (i: typeof insights[number]) => number | undefined) => insights.reduce((t, i) => t + (f(i) || 0), 0);
    const value = sum(i => i.contributedValue), revenue = sum(i => i.contributedRevenue), workCost = sum(i => i.allocatedCostsByType?.['total']);
    return { value, revenue, workCost, revenueReturn: workCost > 0 && revenue > 0 ? revenue / workCost : null };
};

export const statusFor = (utilisation: number | null): WorkforceStatus =>
    utilisation === null ? 'unknown' : utilisation > OVER ? 'over' : utilisation >= STRETCHED ? 'stretched' : utilisation < UNDER ? 'under' : 'healthy';

const percent = (share: number) => `${Math.round(share * 100)}%`;

// how much two sets have in common, each thing counting by its weight (1 when none is given)
const overlap = (a: Set<string>, b: Set<string>, weight: (x: string) => number = () => 1) => {
    if (!a.size || !b.size) return 0;
    let shared = 0, all = 0;
    new Set([...a, ...b]).forEach(x => {
        all += weight(x);
        if (a.has(x) && b.has(x)) shared += weight(x);
    });
    return all ? shared / all : 0;
};

const people_ = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

// the rows and columns with the most in them, at most limit of each
export const matrix = (cells: Map<string, Map<string, number>>, names: Map<string, string>, limit: number, columnLimit = limit, sameAxes = false): WorkforceMatrix => {
    const rowTotals = new Map<string, number>();
    const columnTotals = new Map<string, number>();
    cells.forEach((row, r) => row.forEach((v, c) => {
        rowTotals.set(r, (rowTotals.get(r) || 0) + v);
        columnTotals.set(c, (columnTotals.get(c) || 0) + v);
        if (sameAxes) {
            rowTotals.set(c, (rowTotals.get(c) || 0) + v);
            columnTotals.set(r, (columnTotals.get(r) || 0) + v);
        }
    }));
    const top = (totals: Map<string, number>, n: number) => [...totals.entries()].sort((a, b) => b[1] - a[1]).slice(0, n).map(([key]) => ({ key, name: names.get(key) || 'Unnamed' }));
    const rows = top(rowTotals, limit);
    const columns = sameAxes ? rows : top(columnTotals, columnLimit);
    const values = rows.map(r => columns.map(c => cells.get(r.key)?.get(c.key) || 0));
    return { rows, columns, values, max: Math.max(0, ...values.flat()) };
};

export const buildWorkforceReport = (input: WorkforceInput): WorkforceReport => {
    const { organisationModel, analysis, workspaceKey, modelNames, money, number } = input;
    const insightsOf = (n: LinqsetNode) => getInsightsForNode(n, ORGANISATION_MODEL_KEY, workspaceKey);
    const allRoleNodes = organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE);
    const allPersonNodes = organisationModel.nodes.filter(n => n.superType === NODE_SUPERTYPE.PEOPLE);
    const names = new Map(organisationModel.nodes.map(n => [n._key, n.name || 'Unnamed']));

    // who fills which role, and the share of their time on each (even shares when none are set, as the insights do)
    const fills = organisationModel.edges.filter(e => e.type === EDGE_TYPE.FULFILS && names.has(e.sourceKey) && names.has(e.targetKey));
    const personRoles = new Map<string, { key: string; share: number }[]>();
    allPersonNodes.forEach(p => {
        const edges = fills.filter(e => e.sourceKey === p._key);
        const totalPercentage = edges.reduce((sum, e) => sum + (e.outputPercentage || 0), 0);
        personRoles.set(p._key, edges.map(e => ({ key: e.targetKey, share: totalPercentage ? (e.outputPercentage || 0) / totalPercentage : 1 / edges.length })));
    });
    const headcountOf = (p: LinqsetNode) => (p.type === NODE_TYPE.TEAM ? p.properties?.people || 1 : 1);

    // the work: each action's hours shared between the roles performing it, the systems each role uses there, the
    // models it's in, and the items passed from one role's work to another's
    const roleHours = new Map<string, number>();
    const roleActions = new Map<string, number>();
    const roleModels = new Map<string, Set<string>>();
    const roleSystems = new Map<string, Map<string, number>>();
    const roleFriction = new Map<string, { actions: number; hours: number; cost: number }>();
    const actions = new Map(analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION).map(n => [n._key, n]));
    actions.forEach(a => {
        const roles = a.roles || [];
        roles.forEach(r => {
            const share = (a.hours || 0) / roles.length;
            roleHours.set(r._key, (roleHours.get(r._key) || 0) + share);
            roleActions.set(r._key, (roleActions.get(r._key) || 0) + 1);
            if (isFrictionWork(a)) {
                const x = roleFriction.get(r._key) || roleFriction.set(r._key, { actions: 0, hours: 0, cost: 0 }).get(r._key)!;
                x.actions++;
                x.hours += share;
                x.cost += (a.cost || 0) / roles.length;
            }
            (roleModels.get(r._key) || roleModels.set(r._key, new Set()).get(r._key)!).add(a.modelKey);
            const systems = roleSystems.get(r._key) || roleSystems.set(r._key, new Map()).get(r._key)!;
            (r.systemKeys || []).forEach(s => systems.set(s, (systems.get(s) || 0) + share));
        });
    });
    const handoffCells = new Map<string, Map<string, number>>();
    analysis.edges.forEach(e => {
        const from = actions.get(e.from), to = actions.get(e.to);
        if (!from || !to || !e.items) return;
        (from.roles || []).forEach(rf => (to.roles || []).forEach(rt => {
            if (rf._key === rt._key) return;
            const row = handoffCells.get(rf._key) || handoffCells.set(rf._key, new Map()).get(rf._key)!;
            row.set(rt._key, (row.get(rt._key) || 0) + e.items);
        }));
    });

    // in a scope, the roles with work in it and the people who fill them
    const roleNodes = input.scoped ? allRoleNodes.filter(n => roleActions.has(n._key)) : allRoleNodes;
    const scopeModels = input.scoped ? new Set([...actions.values()].map(a => a.modelKey)) : undefined;
    const inScope = new Set(roleNodes.map(n => n._key));
    const personNodes = input.scoped ? allPersonNodes.filter(p => (personRoles.get(p._key) || []).some(r => inScope.has(r.key))) : allPersonNodes;

    // roles
    const roles: RoleSummary[] = roleNodes.map(n => {
        const i = insightsOf(n);
        const people = fills.filter(e => e.targetKey === n._key).map(e => e.sourceKey);
        const filled = personNodes.filter(p => people.includes(p._key));
        const external = n.type === NODE_TYPE.EXTERNAL_ORGANISATION;
        // capacity is known from the people filling it, or from the role's own hours; "use calculated" just matches the work
        const frequencySet = !!n.properties?.frequency && n.properties.frequency.type !== FREQUENCY_TYPE.USE_CALCULATED;
        // without either, the organisation's full-time year stands in for it, when it has one
        const capacityFromDefault = !external && !i?.hasPeople && !filled.length && !frequencySet && (input.defaultCapacityHours || 0) > 0;
        const capacityKnown = !external && (i?.hasPeople || filled.length > 0 || frequencySet || capacityFromDefault) && (i?.modelledCapacity || 0) > 0;
        const hours = input.scoped ? roleHours.get(n._key) || 0 : i?.hours ?? roleHours.get(n._key) ?? 0;
        // in a scope the role's capacity still has to cover its work in the other models: the organisation's insights
        // hold all its work, so the rest is what isn't in the scope
        const otherHours = input.scoped ? Math.max(0, (i?.hours ?? hours) - hours) : 0;
        const capacity = capacityKnown ? i!.modelledCapacity : null;
        const utilisation = capacity ? (hours + otherHours) / capacity : null;
        const ownCost = total(i?.costAllocation);
        // a role filled by people has their costs, in proportion to the share of time they give it
        const peopleCost = filled.reduce((sum, p) => sum + total(insightsOf(p)?.costAllocation) * (personRoles.get(p._key)?.find(r => r.key === n._key)?.share || 0), 0);
        const cost = ownCost || peopleCost;
        const idleCost = capacity && utilisation !== null && utilisation < 1 ? cost * (1 - utilisation) : 0;
        const systems = [...(roleSystems.get(n._key) || new Map()).entries()].sort((a, b) => b[1] - a[1])
            .map(([key, h]) => ({ key, name: names.get(key) || 'Unnamed', hours: h }));
        return {
            key: n._key, name: n.name || 'Unnamed', external,
            headcount: filled.length ? filled.reduce((sum, p) => sum + headcountOf(p), 0) : (n.properties?.people || i?.numPeople || 0),
            peopleKeys: people, hours, otherHours, capacity, capacityFromDefault: capacityKnown && capacityFromDefault, utilisation, status: statusFor(utilisation), cost, idleCost,
            actions: roleActions.get(n._key) || 0,
            frictionActions: roleFriction.get(n._key)?.actions || 0,
            frictionHours: roleFriction.get(n._key)?.hours || 0,
            frictionCost: roleFriction.get(n._key)?.cost || 0,
            models: [...(roleModels.get(n._key) || [])].map(k => modelNames[k] || 'Unnamed model').sort(),
            systems,
            contribution: contributionOf(n, workspaceKey, scopeModels),
        };
    }).sort((a, b) => (b.utilisation ?? -1) - (a.utilisation ?? -1) || b.hours - a.hours);
    const roleByKey = new Map(roles.map(r => [r.key, r]));

    // people
    const people: PersonSummary[] = personNodes.map(p => {
        const i = insightsOf(p);
        const capacity = i?.modelledCapacity || 0;
        const hours = i?.hours || 0;
        const utilisation = capacity ? hours / capacity : null;
        const filled = (personRoles.get(p._key) || []).map(r => ({ key: r.key, name: names.get(r.key) || 'Unnamed', share: r.share }));
        const flags: string[] = [];
        if (utilisation !== null && utilisation > OVER) flags.push('Over capacity');
        if (!filled.length) flags.push('No role');
        if (filled.length >= SPREAD_ROLES) flags.push(`${filled.length} roles`);
        if (utilisation !== null && utilisation < UNDER && filled.length) flags.push('Spare capacity');
        if (!capacity) flags.push('No hours set');
        return { key: p._key, name: p.name || 'Unnamed', team: p.type === NODE_TYPE.TEAM, headcount: headcountOf(p), roles: filled, hours, capacity, utilisation,
            status: statusFor(utilisation), cost: total(i?.costAllocation), flags };
    }).sort((a, b) => (b.utilisation ?? -1) - (a.utilisation ?? -1));

    // what to look at, most important first
    const findings: WorkforceFinding[] = [];
    const managed = roles.filter(r => !r.external);
    managed.filter(r => r.status === 'over').forEach(r => findings.push({
        severity: 'critical', roleKey: r.key, weight: r.hours + r.otherHours - (r.capacity || 0),
        title: `${r.name} has more work than capacity`,
        detail: `${percent(r.utilisation!)} of its capacity: ${number(r.hours + r.otherHours - r.capacity!)} hours a year more work than its ${r.headcount ? people_(r.headcount) : 'people'} can do${input.defaultCapacityHours ? `, the work of about ${Math.ceil((r.hours + r.otherHours) / input.defaultCapacityHours)} full-time ${Math.ceil((r.hours + r.otherHours) / input.defaultCapacityHours) === 1 ? 'person' : 'people'}` : ''}${r.otherHours ? `, counting ${number(r.otherHours)} hours in other models` : ''}.`,
    }));
    people.filter(p => p.status === 'over').forEach(p => findings.push({
        severity: 'critical', personKey: p.key, weight: p.hours - p.capacity,
        title: `${p.name} is assigned more than their time`,
        detail: `${percent(p.utilisation!)} of their ${number(p.capacity)} hours a year, across ${p.roles.map(r => r.name).join(', ') || 'their roles'}.`,
    }));
    managed.filter(r => r.headcount === 1 && r.hours > 0 && r.actions > 1).forEach(r => findings.push({
        severity: 'warning', roleKey: r.key, weight: r.hours,
        title: `Only one person can do ${r.name}`,
        detail: `${r.actions} actions${r.models.length ? ` in ${r.models.length} model${r.models.length === 1 ? '' : 's'}` : ''} and ${number(r.hours)} hours a year depend on one person.`,
    }));
    managed.filter(r => r.status === 'under' && r.idleCost > 0).forEach(r => findings.push({
        severity: 'warning', roleKey: r.key, weight: r.idleCost,
        title: `${r.name} has much more capacity than work`,
        detail: `${percent(r.utilisation!)} of its capacity is used: about ${money(r.idleCost)} a year of capacity no modelled work needs.`,
    }));
    people.filter(p => p.roles.length >= SPREAD_ROLES).forEach(p => findings.push({
        severity: 'warning', personKey: p.key, weight: p.hours,
        title: `${p.name} is spread across ${p.roles.length} roles`,
        detail: `Switching between ${p.roles.map(r => r.name).join(', ')} can cost focus; check the roles still need them all.`,
    }));
    const noRole = people.filter(p => !p.roles.length);
    if (noRole.length) findings.push({
        severity: 'info', weight: noRole.reduce((sum, p) => sum + p.cost, 0),
        title: `${noRole.length} ${noRole.length === 1 ? 'person has' : 'people have'} no role`,
        detail: `${noRole.slice(0, 5).map(p => p.name).join(', ')}${noRole.length > 5 ? ' and others' : ''}: their time isn't connected to any work.`,
    });
    const unknown = managed.filter(r => r.capacity === null && r.hours > 0);
    if (unknown.length) findings.push({
        severity: 'info', weight: unknown.reduce((sum, r) => sum + r.hours, 0),
        title: `${unknown.length} ${unknown.length === 1 ? 'role has' : 'roles have'} no capacity set`,
        detail: `${unknown.slice(0, 5).map(r => r.name).join(', ')}${unknown.length > 5 ? ' and others' : ''}: add their people or hours to see whether they can do their work.`,
    });
    const fromDefault = managed.filter(r => r.capacityFromDefault && r.hours > 0);
    if (fromDefault.length) findings.push({
        severity: 'info', weight: fromDefault.reduce((sum, r) => sum + r.hours, 0),
        title: `${fromDefault.length} ${fromDefault.length === 1 ? "role's capacity is" : "roles' capacities are"} the organisation's full-time year`,
        detail: `${fromDefault.slice(0, 5).map(r => r.name).join(', ')}${fromDefault.length > 5 ? ' and others' : ''}: each has ${number(input.defaultCapacityHours || 0)} hours a year for each of its people. Set their own hours where they differ.`,
    });
    const order: FindingSeverity[] = ['critical', 'warning', 'info'];
    findings.sort((a, b) => order.indexOf(a.severity) - order.indexOf(b.severity) || b.weight - a.weight);

    // matching people to roles: people with spare time whose roles use the same systems and models are the likeliest
    // to pick up an overloaded role's work, or to cover for the one person who can do a role
    // a system most roles use (email, say) says little about someone's skills; one few roles use says much more
    const rolesUsing = new Map<string, number>();
    managed.forEach(r => r.systems.forEach(s => rolesUsing.set(s.key, (rolesUsing.get(s.key) || 0) + 1)));
    const specialism = (systemKey: string) => Math.log((managed.length + 1) / ((rolesUsing.get(systemKey) || 0) + 1));
    const profile = (roleKeys: string[]) => ({
        systems: new Set(roleKeys.flatMap(k => roleByKey.get(k)?.systems.map(s => s.key) || [])),
        models: new Set(roleKeys.flatMap(k => roleByKey.get(k)?.models || [])),
    });
    const matches: WorkforceMatch[] = [];
    const needing = [
        ...managed.filter(r => r.status === 'over').map(r => ({ r, reason: 'over' as const })),
        ...managed.filter(r => r.headcount === 1 && r.hours > 0 && r.actions > 1 && r.status !== 'over').map(r => ({ r, reason: 'single' as const })),
    ].slice(0, 12);
    needing.forEach(({ r, reason }) => {
        const target = profile([r.key]);
        const candidates = people.filter(p => !r.peopleKeys.includes(p.key) && p.roles.length > 0)
            .map(p => {
                const own = profile(p.roles.map(x => x.key));
                // the most specialised first, as they say most about the fit
                const sharedSystems = [...target.systems].filter(s => own.systems.has(s)).sort((a, b) => specialism(b) - specialism(a)).map(s => names.get(s) || 'Unnamed');
                const sharedModels = [...target.models].filter(m => own.models.has(m));
                const spareHours = Math.max(0, p.capacity - p.hours);
                return { key: p.key, name: p.name, currentRoles: p.roles.map(x => x.name), spareHours,
                    similarity: 0.6 * overlap(target.systems, own.systems, specialism) + 0.4 * overlap(target.models, own.models), sharedSystems, sharedModels };
            })
            // to take on work they need spare time; to cover for someone, some time to learn the role
            .filter(c => c.similarity > 0 && c.spareHours >= (reason === 'over' ? MIN_SPARE_HOURS : 1))
            .sort((a, b) => b.similarity - a.similarity || b.spareHours - a.spareHours)
            .slice(0, 3);
        matches.push({ roleKey: r.key, role: r.name, reason, candidates });
    });

    // totals, over the roles whose capacity is known
    const known = managed.filter(r => r.capacity !== null);
    const knownHours = known.reduce((sum, r) => sum + r.hours, 0);
    const knownOtherHours = known.reduce((sum, r) => sum + r.otherHours, 0);
    const knownCapacity = known.reduce((sum, r) => sum + (r.capacity || 0), 0);
    const headcount = personNodes.length ? personNodes.reduce((sum, p) => sum + headcountOf(p), 0) : managed.reduce((sum, r) => sum + r.headcount, 0);
    // what the roles cost (their own, or their people's share) and the people with no role, whose cost no role carries
    const cost = managed.reduce((sum, r) => sum + r.cost, 0) + noRole.reduce((sum, p) => sum + p.cost, 0);

    const handoffs = matrix(handoffCells, names, 12, 12, true);
    const topHandoffs = [...handoffCells.entries()].flatMap(([f, row]) => [...row.entries()].map(([t, items]) => ({ from: names.get(f) || 'Unnamed', to: names.get(t) || 'Unnamed', items })))
        .sort((a, b) => b.items - a.items).slice(0, 8);
    const systemCells = new Map(managed.filter(r => r.systems.length).map(r => [r.key, new Map(r.systems.map(s => [s.key, s.hours]))]));

    return {
        totals: {
            headcount, people: personNodes.length, roles: managed.length,
            cost, hours: knownHours, otherHours: knownOtherHours, capacity: knownCapacity,
            utilisation: knownCapacity ? (knownHours + knownOtherHours) / knownCapacity : null,
            spareHours: Math.max(0, knownCapacity - knownHours - knownOtherHours),
            spareCost: managed.reduce((sum, r) => sum + r.idleCost, 0),
            overRoles: managed.filter(r => r.status === 'over').length,
            unknownRoles: managed.filter(r => r.capacity === null).length,
            defaultRoles: managed.filter(r => r.capacityFromDefault).length,
            frictionActions: [...actions.values()].filter(a => isFrictionWork(a)).length,
            frictionHours: [...actions.values()].filter(a => isFrictionWork(a)).reduce((sum, a) => sum + (a.hours || 0), 0),
            frictionCost: [...actions.values()].filter(a => isFrictionWork(a)).reduce((sum, a) => sum + (a.cost || 0), 0),
            frictionRoles: managed.filter(r => r.frictionActions > 0).length,
            valueContribution: roles.reduce((sum, r) => sum + r.contribution.value, 0),
            revenueContribution: roles.reduce((sum, r) => sum + r.contribution.revenue, 0),
            workCost: roles.reduce((sum, r) => sum + r.contribution.workCost, 0),
        },
        scoped: !!input.scoped,
        findings, roles, people, handoffs, topHandoffs,
        systems: matrix(systemCells, names, 12, 10),
        matches,
    };
};
